import React, { useEffect, useMemo, useRef, useState } from "react";
import { orgAgents, orgDivisions, orgHandoffs, orgRanks } from "../assets/files/agentOrg";

// โหมด "เส้นทางส่งงาน" แบบกาแล็กซี: Tech Lead เป็นดวงอาทิตย์กลางฉาก
// ตำแหน่งแต่ละระดับโคจรคนละวง (L2 ใกล้สุด → L4 ไกลสุด) หมุนช้า ๆ คนละความเร็ว
// เส้นส่งงานยืดตามโหนดทุกเฟรมผ่าน requestAnimationFrame (แก้ DOM ตรง ๆ ไม่ re-render React)
// ชี้/คลิกโหนด = หยุดหมุนชั่วคราว + ไฮไลต์เฉพาะเส้นที่เกี่ยวข้อง

const STYLE_ID = "ptn-org-galaxy-style";
const CSS = `
.ptn-radial { --node-size: 56px; --hub-size: 84px; --tilt-x: 52deg; --tilt-y: 0deg; color: #e6eef7; }

/* ฉาก 3 มิติ */
.ptn-radial__scene {
  position: relative;
  width: min(100%, 820px);
  aspect-ratio: 4 / 3;
  margin: 0 auto;
  perspective: 1500px;
  perspective-origin: 50% 28%;
  overflow: visible;
}
.ptn-radial__stars { position: absolute; inset: -4%; width: 108%; height: 108%; pointer-events: none; }
.ptn-radial__star { fill: #ffffff; animation: ptnTwinkle var(--tw-dur, 3s) ease-in-out infinite; animation-delay: var(--tw-delay, 0s); }
@keyframes ptnTwinkle { 0%, 100% { opacity: 0.15; } 50% { opacity: 0.9; } }
.ptn-radial__stage {
  position: absolute;
  left: 50%;
  top: 52%;
  width: 94%;
  aspect-ratio: 1 / 1;
  transform-style: preserve-3d;
  transform: translate(-50%, -50%) rotateX(var(--tilt-x)) rotateY(var(--tilt-y));
  transition: transform 0.35s ease-out;
}
/* เนบิวลาใต้ระนาบ */
.ptn-radial__floor {
  position: absolute;
  inset: -8%;
  border-radius: 50%;
  background:
    radial-gradient(circle at 50% 50%, rgba(255, 214, 120, 0.28) 0%, rgba(255, 170, 60, 0.10) 14%, transparent 24%),
    radial-gradient(circle at 50% 50%, rgba(12, 118, 229, 0.22) 20%, rgba(74, 69, 173, 0.16) 45%, rgba(11, 42, 74, 0) 72%);
  pointer-events: none;
}
.ptn-radial__svg { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }

/* วงโคจร */
.ptn-radial__orbit { fill: none; stroke: rgba(160, 200, 255, 0.22); stroke-width: 1.4; stroke-dasharray: 3 9; }
.ptn-radial__orbit-glow { fill: none; stroke: rgba(120, 180, 255, 0.10); stroke-width: 14; }
.ptn-radial__orbit-label { font-size: 20px; font-weight: 800; letter-spacing: 0.14em; fill: rgba(190, 215, 245, 0.75); text-transform: uppercase; }
.ptn-radial__orbit-sub { font-size: 17px; fill: rgba(190, 215, 245, 0.5); }

/* เส้นส่งงาน + แสงวิ่ง */
.ptn-radial__edge {
  fill: none;
  stroke-width: 2.2;
  stroke-linecap: round;
  transition: opacity 0.25s ease, stroke-width 0.25s ease;
  cursor: pointer;
}
.ptn-radial__edge--feedback { stroke-dasharray: 6 6; }
.ptn-radial__edge--on { stroke-width: 3.6; }
.ptn-radial__streak {
  fill: none;
  stroke: #ffffff;
  stroke-width: 3.4;
  stroke-linecap: round;
  stroke-dasharray: 9 91;
  animation: ptnStreak var(--streak-dur, 3s) linear infinite;
  animation-delay: var(--streak-delay, 0s);
  transition: opacity 0.25s ease;
  pointer-events: none;
}
.ptn-radial__streak--spoke { stroke: #ffe6a3; stroke-width: 2.6; stroke-dasharray: 6 94; }
@keyframes ptnStreak { from { stroke-dashoffset: 100; } to { stroke-dashoffset: 0; } }
.ptn-radial__spoke { fill: none; stroke: #e2b85c; stroke-width: 1.4; stroke-dasharray: 2 8; stroke-linecap: round; transition: opacity 0.25s ease; }
.ptn-radial__wave { fill: none; stroke: #ffd36b; stroke-width: 1.6; }
.ptn-radial__halo { fill: url(#ptn-halo); transition: opacity 0.25s ease; }

/* โหนด: ตั้งขึ้นสวนการเอียง ฐานอยู่บนจุดพิกัด */
.ptn-radial__node {
  position: absolute;
  transform-origin: 50% 100%;
  transform: translate(-50%, -100%) rotateY(calc(-1 * var(--tilt-y))) rotateX(calc(-1 * var(--tilt-x)));
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  width: 124px;
  border: 0;
  background: transparent;
  padding: 0 0 2px;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.25s ease;
  will-change: left, top;
}
.ptn-radial__node--dim { opacity: 0.18; }
.ptn-radial__node:focus-visible { outline: none; }
.ptn-radial__icon {
  width: var(--node-size);
  height: var(--node-size);
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 1.1rem;
  background: var(--div-gradient);
  border: 3px solid rgba(255,255,255,0.85);
  box-shadow: 0 0 0 3px rgba(255,255,255,0.08), 0 0 24px var(--div-glow), 0 12px 24px rgba(2, 12, 30, 0.5), inset 0 -4px 8px rgba(0,0,0,0.2), inset 0 3px 6px rgba(255,255,255,0.4);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.ptn-radial__node:hover .ptn-radial__icon,
.ptn-radial__node--on .ptn-radial__icon {
  transform: scale(1.14) translateY(-3px);
  box-shadow: 0 0 0 5px var(--div-glow), 0 0 40px var(--div-glow), 0 18px 30px rgba(2, 12, 30, 0.6);
}
.ptn-radial__name {
  font-size: 0.72rem;
  font-weight: 700;
  color: #0b2a4a;
  line-height: 1.15;
  text-align: center;
  padding: 0.16rem 0.55rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255,255,255,0.6);
  box-shadow: 0 4px 12px rgba(2, 12, 30, 0.45);
  white-space: nowrap;
}
.ptn-radial__node--on .ptn-radial__name { color: var(--div-color); box-shadow: 0 0 0 2px var(--div-color), 0 4px 12px rgba(2, 12, 30, 0.45); }

/* ดวงอาทิตย์: Tech Lead */
.ptn-radial__node--hub .ptn-radial__icon {
  width: var(--hub-size);
  height: var(--hub-size);
  font-size: 1.6rem;
  background: radial-gradient(circle at 35% 30%, #fff1c2 0%, #f3c45a 35%, #c98a2a 100%);
  border-color: rgba(255, 240, 200, 0.9);
  box-shadow: 0 0 0 6px rgba(255, 210, 100, 0.18), 0 0 40px rgba(255, 190, 70, 0.75), 0 0 90px rgba(255, 160, 40, 0.45), 0 14px 30px rgba(2, 12, 30, 0.6);
  animation: ptnSun 3.8s ease-in-out infinite;
}
.ptn-radial__node--hub .ptn-radial__name {
  font-size: 0.82rem;
  color: #6b4a0c;
  background: linear-gradient(135deg, #fffdf5 0%, #f8e2ac 100%);
  border-color: rgba(255, 230, 160, 0.9);
}
@keyframes ptnSun {
  0%, 100% { transform: translateY(0); box-shadow: 0 0 0 6px rgba(255, 210, 100, 0.18), 0 0 40px rgba(255, 190, 70, 0.75), 0 0 90px rgba(255, 160, 40, 0.45), 0 14px 30px rgba(2, 12, 30, 0.6); }
  50% { transform: translateY(-8px); box-shadow: 0 0 0 12px rgba(255, 210, 100, 0.12), 0 0 60px rgba(255, 190, 70, 0.95), 0 0 130px rgba(255, 160, 40, 0.6), 0 26px 40px rgba(2, 12, 30, 0.6); }
}

/* คำอธิบาย + ปุ่มหมุน */
.ptn-radial__legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.5rem 1.2rem;
  margin-top: 0.4rem;
  font-size: 0.72rem;
  color: rgba(230, 238, 247, 0.78);
}
.ptn-radial__legend-item { display: inline-flex; align-items: center; gap: 0.45rem; }
.ptn-radial__legend-line { width: 30px; height: 0; border-top: 2.5px solid #8f8aff; border-radius: 2px; }
.ptn-radial__legend-line--dash { border-top-style: dashed; }
.ptn-radial__legend-line--gold { border-top: 2px dotted #ffd36b; }
.ptn-radial__spin {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.08);
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 700;
  font-family: inherit;
}
.ptn-radial__spin:hover { background: rgba(255,255,255,0.16); }

/* แผงส่ง/รับงาน (การ์ดสว่างบนพื้นมืด) */
.ptn-radial__panel {
  margin-top: 1rem;
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.14);
  background: #ffffff;
  box-shadow: 0 20px 40px rgba(2, 12, 30, 0.45);
  overflow: hidden;
  color: #1b3950;
}
.ptn-radial__panel-head {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.9rem 1.1rem;
  background: var(--div-soft);
  border-bottom: 1px solid rgba(15, 102, 182, 0.10);
}
.ptn-radial__panel-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: var(--div-gradient);
  box-shadow: 0 8px 16px var(--div-glow);
  flex-shrink: 0;
}
.ptn-radial__panel-title { font-weight: 800; font-size: 0.95rem; color: #0b2a4a; line-height: 1.15; }
.ptn-radial__panel-sub { font-size: 0.74rem; color: #4f6d83; }
.ptn-radial__panel-body { display: grid; grid-template-columns: 1fr 1fr; }
.ptn-radial__col { padding: 0.9rem 1.1rem; }
.ptn-radial__col + .ptn-radial__col { border-left: 1px dashed rgba(15, 102, 182, 0.14); }
.ptn-radial__col-label {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #0b4f84;
  margin-bottom: 0.55rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.ptn-radial__flow {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.45rem 0.55rem;
  margin-bottom: 0.4rem;
  border: 1px solid rgba(15, 102, 182, 0.10);
  border-radius: 12px;
  background: #fbfdff;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.ptn-radial__flow:hover { transform: translateY(-1px); border-color: var(--div-color); box-shadow: 0 8px 16px var(--div-glow); }
.ptn-radial__flow-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.8rem;
  background: var(--div-gradient);
  flex-shrink: 0;
}
.ptn-radial__flow-name { font-size: 0.8rem; font-weight: 700; color: #0b2a4a; line-height: 1.15; }
.ptn-radial__flow-label { font-size: 0.7rem; color: #6b8299; line-height: 1.3; }
.ptn-radial__flow-type {
  margin-left: auto;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  white-space: nowrap;
  background: #eef4fb;
  color: #4f6d83;
}
.ptn-radial__flow-type--feedback { background: #fff7e6; color: #a76a12; }
.ptn-radial__empty { font-size: 0.78rem; color: #8299ab; }
.ptn-radial__hint { margin-top: 0.9rem; text-align: center; font-size: 0.78rem; color: rgba(230, 238, 247, 0.7); }
.ptn-radial__detail-btn {
  margin-left: auto;
  border: 1px solid var(--div-color);
  background: #ffffff;
  color: var(--div-color);
  font-weight: 700;
  font-size: 0.76rem;
  padding: 0.4rem 0.8rem;
  border-radius: 10px;
  white-space: nowrap;
}
.ptn-radial__detail-btn:hover { background: var(--div-color); color: #ffffff; }

@media (prefers-reduced-motion: reduce) {
  .ptn-radial__node--hub .ptn-radial__icon, .ptn-radial__star { animation: none !important; }
  .ptn-radial__streak { animation: none !important; opacity: 0 !important; }
  .ptn-radial__wave { display: none; }
  .ptn-radial__stage { transition: none; }
}
@media (max-width: 575.98px) {
  .ptn-radial { --node-size: 42px; --hub-size: 62px; --tilt-x: 44deg; }
  .ptn-radial__scene { aspect-ratio: 1 / 1; perspective: 1100px; }
  .ptn-radial__stage { width: 100%; }
  .ptn-radial__node { width: 92px; }
  .ptn-radial__icon { font-size: 0.88rem; border-width: 2px; }
  .ptn-radial__name { font-size: 0.6rem; padding: 0.1rem 0.35rem; }
  .ptn-radial__panel-body { grid-template-columns: 1fr; }
  .ptn-radial__col + .ptn-radial__col { border-left: 0; border-top: 1px dashed rgba(15, 102, 182, 0.14); }
}
`;

if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const tag = document.createElement("style");
  tag.id = STYLE_ID;
  tag.innerHTML = CSS;
  document.head.appendChild(tag);
}

const LEAD_DIVISION = {
  key: "lead",
  color: "#c98a2a",
  soft: "#fff6dc",
  glow: "rgba(255, 190, 70, 0.45)",
  gradient: "linear-gradient(135deg, #f3c45a 0%, #c98a2a 100%)",
};

const SIZE = 1000;
const CENTER = SIZE / 2;
const NODE_PAD = 44; // ระยะถอยจากศูนย์กลางโหนด ให้หัวลูกศรหยุดที่ขอบ (หน่วย viewBox)
const HUB_PAD = 70;

// วงโคจรตามระดับ: รัศมี ความเร็ว (เรเดียน/วินาที; ลบ = ทวนเข็ม) และมุมเริ่มต้น
const ORBITS = {
  L2: { radius: 165, speed: 0.085, phase: -Math.PI / 2 },
  L3: { radius: 300, speed: -0.05, phase: -Math.PI / 2 + Math.PI / 6 },
  L4: { radius: 430, speed: 0.035, phase: -Math.PI / 2 },
};

const divisionVars = (division) => ({
  "--div-color": division.color,
  "--div-soft": division.soft,
  "--div-glow": division.glow,
  "--div-gradient": division.gradient,
});

const agentById = Object.fromEntries(orgAgents.map((agent) => [agent.id, agent]));
const divisionByKey = Object.fromEntries(orgDivisions.map((division) => [division.key, division]));
const divisionOf = (agent) => divisionByKey[agent.division] || LEAD_DIVISION;
const rankByKey = Object.fromEntries(orgRanks.map((rank) => [rank.key, rank]));

const LEAD = orgAgents.find((agent) => agent.rank === "L1");
const MEMBERS = orgAgents.filter((agent) => agent.rank !== "L1");
// สมาชิกในแต่ละวง (ตามลำดับในไฟล์ข้อมูล)
const ORBIT_MEMBERS = Object.keys(ORBITS).map((rankKey) => ({
  rankKey,
  ...ORBITS[rankKey],
  ids: MEMBERS.filter((agent) => agent.rank === rankKey).map((agent) => agent.id),
}));

// ตำแหน่งทุกโหนด ณ เวลา t (วินาที)
const computePositions = (t) => {
  const map = { [LEAD.id]: { x: CENTER, y: CENTER } };
  ORBIT_MEMBERS.forEach(({ ids, radius, speed, phase }) => {
    ids.forEach((id, index) => {
      const angle = phase + speed * t + (2 * Math.PI * index) / ids.length;
      map[id] = { x: CENTER + radius * Math.cos(angle), y: CENTER + radius * Math.sin(angle) };
    });
  });
  return map;
};

const shrink = (from, toward, pad) => {
  const dx = toward.x - from.x;
  const dy = toward.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: from.x + (dx / len) * pad, y: from.y + (dy / len) * pad };
};

// เส้นโค้ง A → B: โค้งออกด้านข้างเล็กน้อย (ตั้งฉากกับทิศทาง) จะได้ไม่พุ่งทะลุดวงอาทิตย์
const chordPath = (a, b) => {
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const bend = Math.min(90, len * 0.22);
  const ctrl = { x: mid.x - (dy / len) * bend, y: mid.y + (dx / len) * bend };
  const start = shrink(a, ctrl, NODE_PAD);
  const end = shrink(b, ctrl, NODE_PAD);
  return `M ${start.x.toFixed(1)} ${start.y.toFixed(1)} Q ${ctrl.x.toFixed(1)} ${ctrl.y.toFixed(1)} ${end.x.toFixed(1)} ${end.y.toFixed(1)}`;
};

const spokePath = (node) => {
  const start = shrink({ x: CENTER, y: CENTER }, node, HUB_PAD);
  const end = shrink(node, { x: CENTER, y: CENTER }, NODE_PAD);
  return `M ${start.x.toFixed(1)} ${start.y.toFixed(1)} L ${end.x.toFixed(1)} ${end.y.toFixed(1)}`;
};

const pct = (value) => `${(value / SIZE) * 100}%`;

// ดาวพื้นหลัง: สุ่มครั้งเดียวตอนโหลดโมดูล
const STARS = Array.from({ length: 70 }, (_, index) => {
  const seed = (index * 9301 + 49297) % 233280;
  const rand = (k) => ((seed * (k + 1) * 7919) % 10007) / 10007;
  return { x: rand(1) * SIZE, y: rand(2) * SIZE, r: 1.2 + rand(3) * 2.2, dur: 2.2 + rand(4) * 3.5, delay: -rand(5) * 5 };
});

const FlowItem = ({ agent, label, type, onPick }) => {
  const division = divisionOf(agent);
  return (
    <button type="button" className="ptn-radial__flow" style={divisionVars(division)} onClick={() => onPick(agent.id)}>
      <span className="ptn-radial__flow-icon">
        <i className={`fa-solid ${agent.icon}`} />
      </span>
      <span className="d-flex flex-column" style={{ minWidth: 0 }}>
        <span className="ptn-radial__flow-name">{agent.title}</span>
        <span className="ptn-radial__flow-label">{label}</span>
      </span>
      <span className={`ptn-radial__flow-type${type === "feedback" ? " ptn-radial__flow-type--feedback" : ""}`}>
        {type === "feedback" ? "แจ้งกลับ" : "ส่งงาน"}
      </span>
    </button>
  );
};

export default function OrgHandoffRadial({ onDetail }) {
  const [focus, setFocus] = useState(null);
  const [hover, setHover] = useState(null);
  const [spinning, setSpinning] = useState(true);
  const [tilt, setTilt] = useState({ x: 52, y: 0 });
  const active = hover || focus;

  // นาฬิกาของวงโคจร + ตำแหน่งล่าสุด (เก็บใน ref เพื่อไม่ให้ re-render ทุกเฟรม)
  const clockRef = useRef(0);
  const speedMulRef = useRef(1);
  const pausedRef = useRef(false);
  const positionsRef = useRef(computePositions(0));
  const nodeRefs = useRef(new Map());
  const haloRefs = useRef(new Map());
  const edgeRefs = useRef(new Map());
  const spokeRefs = useRef(new Map());

  pausedRef.current = !spinning || Boolean(active);

  const edges = useMemo(
    () =>
      orgHandoffs
        .filter((edge) => agentById[edge.from] && agentById[edge.to])
        .map((edge, index) => ({ ...edge, key: `${edge.from}->${edge.to}-${index}`, division: divisionOf(agentById[edge.from]) })),
    []
  );

  // ลูปแอนิเมชัน: ขยับโหนด/เส้นด้วย DOM โดยตรง
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    let frame = 0;
    let last = performance.now();
    const loop = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      // ชะลอ/เร่งนุ่ม ๆ ตอนหยุดหรือเริ่มหมุน
      speedMulRef.current += ((pausedRef.current ? 0 : 1) - speedMulRef.current) * 0.06;
      clockRef.current += dt * speedMulRef.current;

      if (speedMulRef.current > 0.001) {
        const positions = computePositions(clockRef.current);
        positionsRef.current = positions;

        nodeRefs.current.forEach((el, id) => {
          if (!el || !positions[id]) return;
          el.style.left = pct(positions[id].x);
          el.style.top = pct(positions[id].y);
        });
        haloRefs.current.forEach((el, id) => {
          if (!el || !positions[id]) return;
          el.setAttribute("cx", positions[id].x);
          el.setAttribute("cy", positions[id].y);
        });
        edgeRefs.current.forEach((els, key) => {
          const edge = els.edge;
          if (!edge) return;
          const d = chordPath(positions[edge.from], positions[edge.to]);
          els.path?.setAttribute("d", d);
          els.streak?.setAttribute("d", d);
        });
        spokeRefs.current.forEach((els, id) => {
          const d = spokePath(positions[id]);
          els.path?.setAttribute("d", d);
          els.streak?.setAttribute("d", d);
        });
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, []);

  const handleSceneMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: 52 - py * 10, y: px * 14 });
  };
  const handleSceneLeave = () => setTilt({ x: 52, y: 0 });

  const related = useMemo(() => {
    if (!active) return null;
    if (active === LEAD.id) return new Set(orgAgents.map((agent) => agent.id));
    const set = new Set([active, LEAD.id]);
    edges.forEach((edge) => {
      if (edge.from === active) set.add(edge.to);
      if (edge.to === active) set.add(edge.from);
    });
    return set;
  }, [active, edges]);

  const isEdgeOn = (edge) => active && (edge.from === active || edge.to === active);
  const edgeOpacity = (edge) => (!active ? 0.5 : isEdgeOn(edge) ? 1 : 0.05);
  const hubActive = active === LEAD.id;

  const focusAgent = focus ? agentById[focus] : null;
  const outgoing = focus ? edges.filter((edge) => edge.from === focus) : [];
  const incoming = focus ? edges.filter((edge) => edge.to === focus) : [];
  const pick = (id) => setFocus((current) => (current === id ? null : id));

  const positions = positionsRef.current;
  const setEdgeRef = (key, edge, slot) => (el) => {
    const entry = edgeRefs.current.get(key) || { edge };
    entry[slot] = el;
    edgeRefs.current.set(key, entry);
  };
  const setSpokeRef = (id, slot) => (el) => {
    const entry = spokeRefs.current.get(id) || {};
    entry[slot] = el;
    spokeRefs.current.set(id, entry);
  };

  return (
    <div className="ptn-radial" style={{ "--tilt-x": `${tilt.x}deg`, "--tilt-y": `${tilt.y}deg` }}>
      <div className="ptn-radial__scene" onMouseMove={handleSceneMove} onMouseLeave={handleSceneLeave}>
        {/* ดาวพื้นหลัง (ชั้นแบน ไม่เอียง) */}
        <svg className="ptn-radial__stars" viewBox={`0 0 ${SIZE} ${SIZE}`} preserveAspectRatio="none" aria-hidden="true">
          {STARS.map((star, index) => (
            <circle key={index} className="ptn-radial__star" cx={star.x} cy={star.y} r={star.r} style={{ "--tw-dur": `${star.dur}s`, "--tw-delay": `${star.delay}s` }} />
          ))}
        </svg>

        <div className="ptn-radial__stage">
          <div className="ptn-radial__floor" />
          <svg className="ptn-radial__svg" viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true">
            <defs>
              <filter id="ptn-glow" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <radialGradient id="ptn-halo">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
                <stop offset="60%" stopColor="#9cc8ff" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#9cc8ff" stopOpacity="0" />
              </radialGradient>
              {[...orgDivisions, LEAD_DIVISION].map((division) => (
                <marker key={division.key} id={`ptn-arrow-${division.key}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={division.color} />
                </marker>
              ))}
            </defs>

            {/* วงโคจร + ป้ายระดับ */}
            {ORBIT_MEMBERS.map(({ rankKey, radius }) => (
              <g key={rankKey}>
                <circle className="ptn-radial__orbit-glow" cx={CENTER} cy={CENTER} r={radius} />
                <circle className="ptn-radial__orbit" cx={CENTER} cy={CENTER} r={radius} />
                <text className="ptn-radial__orbit-label" x={CENTER} y={CENTER - radius - 26} textAnchor="middle">
                  {rankKey} · {rankByKey[rankKey]?.label}
                </text>
                <text className="ptn-radial__orbit-sub" x={CENTER} y={CENTER - radius - 6} textAnchor="middle">
                  {rankByKey[rankKey]?.labelTh}
                </text>
              </g>
            ))}

            {/* คลื่นแสงจากดวงอาทิตย์ */}
            {[0, 1.4, 2.8].map((delay) => (
              <circle key={`wave-${delay}`} className="ptn-radial__wave" cx={CENTER} cy={CENTER} r={HUB_PAD}>
                <animate attributeName="r" from={HUB_PAD} to={ORBITS.L4.radius} dur="4.2s" begin={`${delay}s`} repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.5" to="0" dur="4.2s" begin={`${delay}s`} repeatCount="indefinite" />
              </circle>
            ))}

            {/* แสงใต้โหนด */}
            {MEMBERS.map((agent) => (
              <circle
                key={`halo-${agent.id}`}
                ref={(el) => haloRefs.current.set(agent.id, el)}
                className="ptn-radial__halo"
                cx={positions[agent.id].x}
                cy={positions[agent.id].y}
                r="52"
                style={{ opacity: related && !related.has(agent.id) ? 0.1 : 0.9 }}
              />
            ))}

            {/* เส้นมอบหมายจากดวงอาทิตย์ */}
            {MEMBERS.map((agent, index) => (
              <React.Fragment key={`spoke-${agent.id}`}>
                <path ref={setSpokeRef(agent.id, "path")} className="ptn-radial__spoke" d={spokePath(positions[agent.id])} style={{ opacity: hubActive ? 0.9 : active ? 0.05 : 0.3 }} />
                <path
                  ref={setSpokeRef(agent.id, "streak")}
                  className="ptn-radial__streak ptn-radial__streak--spoke"
                  d={spokePath(positions[agent.id])}
                  pathLength="100"
                  filter="url(#ptn-glow)"
                  style={{ "--streak-dur": "2.6s", "--streak-delay": `${-(index * 0.37)}s`, opacity: hubActive ? 1 : active ? 0 : 0.6 }}
                />
              </React.Fragment>
            ))}

            {/* เส้นส่งงานระหว่างตำแหน่ง */}
            {edges.map((edge) => (
              <path
                key={edge.key}
                ref={setEdgeRef(edge.key, edge, "path")}
                className={`ptn-radial__edge${edge.type === "feedback" ? " ptn-radial__edge--feedback" : ""}${isEdgeOn(edge) ? " ptn-radial__edge--on" : ""}`}
                d={chordPath(positions[edge.from], positions[edge.to])}
                stroke={edge.division.color}
                markerEnd={`url(#ptn-arrow-${edge.division.key})`}
                style={{ opacity: edgeOpacity(edge), pointerEvents: "stroke" }}
                onClick={() => pick(edge.from)}
              >
                <title>
                  {agentById[edge.from].title} → {agentById[edge.to].title}: {edge.label}
                </title>
              </path>
            ))}
            {edges.map((edge, index) => (
              <path
                key={`streak-${edge.key}`}
                ref={setEdgeRef(edge.key, edge, "streak")}
                className="ptn-radial__streak"
                d={chordPath(positions[edge.from], positions[edge.to])}
                pathLength="100"
                filter="url(#ptn-glow)"
                style={{
                  "--streak-dur": edge.type === "feedback" ? "4.2s" : "3.1s",
                  "--streak-delay": `${-(index * 0.53) % 3.1}s`,
                  opacity: !active ? 0.85 : isEdgeOn(edge) ? 1 : 0,
                }}
              />
            ))}
          </svg>

          {/* ดวงอาทิตย์: Tech Lead */}
          <button
            type="button"
            className={`ptn-radial__node ptn-radial__node--hub${hubActive ? " ptn-radial__node--on" : ""}`}
            style={{ left: "50%", top: "50%", ...divisionVars(LEAD_DIVISION) }}
            onClick={() => pick(LEAD.id)}
            onMouseEnter={() => setHover(LEAD.id)}
            onMouseLeave={() => setHover(null)}
            title="มอบหมายงานให้ทุกตำแหน่ง"
          >
            <span className="ptn-radial__icon">
              <i className={`fa-solid ${LEAD.icon}`} />
            </span>
            <span className="ptn-radial__name">{LEAD.title}</span>
          </button>

          {/* ดาวบริวาร: ตำแหน่งบนวงโคจร */}
          {MEMBERS.map((agent) => {
            const division = divisionOf(agent);
            const point = positions[agent.id];
            const on = active === agent.id;
            const dim = related && !related.has(agent.id);
            return (
              <button
                key={agent.id}
                ref={(el) => nodeRefs.current.set(agent.id, el)}
                type="button"
                className={`ptn-radial__node${on ? " ptn-radial__node--on" : ""}${dim ? " ptn-radial__node--dim" : ""}`}
                style={{ left: pct(point.x), top: pct(point.y), ...divisionVars(division) }}
                onClick={() => pick(agent.id)}
                onMouseEnter={() => setHover(agent.id)}
                onMouseLeave={() => setHover(null)}
                title={`${agent.titleTh} · ${agent.rank}`}
              >
                <span className="ptn-radial__icon">
                  <i className={`fa-solid ${agent.icon}`} />
                </span>
                <span className="ptn-radial__name">{agent.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="ptn-radial__legend">
        <span className="ptn-radial__legend-item">
          <span className="ptn-radial__legend-line" /> ส่งงานต่อ (สีตามส่วนงานผู้ส่ง)
        </span>
        <span className="ptn-radial__legend-item">
          <span className="ptn-radial__legend-line ptn-radial__legend-line--dash" /> แจ้งกลับ / ขอความช่วยเหลือ
        </span>
        <span className="ptn-radial__legend-item">
          <span className="ptn-radial__legend-line ptn-radial__legend-line--gold" /> Tech Lead มอบหมาย
        </span>
        <button type="button" className="ptn-radial__spin" onClick={() => setSpinning((value) => !value)}>
          <i className={`fa-solid ${spinning ? "fa-pause" : "fa-play"}`} />
          {spinning ? "หยุดหมุน" : "หมุนต่อ"}
        </button>
      </div>

      {focusAgent ? (
        <div className="ptn-radial__panel" style={divisionVars(divisionOf(focusAgent))}>
          <div className="ptn-radial__panel-head">
            <span className="ptn-radial__panel-icon">
              <i className={`fa-solid ${focusAgent.icon}`} />
            </span>
            <span className="d-flex flex-column" style={{ minWidth: 0 }}>
              <span className="ptn-radial__panel-title">{focusAgent.title}</span>
              <span className="ptn-radial__panel-sub">{focusAgent.titleTh}</span>
            </span>
            <button type="button" className="ptn-radial__detail-btn" onClick={() => onDetail(focusAgent)}>
              <i className="fa-solid fa-circle-info me-1" />
              รายละเอียด
            </button>
          </div>
          <div className="ptn-radial__panel-body">
            <div className="ptn-radial__col">
              <div className="ptn-radial__col-label">
                <i className="fa-solid fa-arrow-right-from-bracket" />
                ส่งงานให้
              </div>
              {focusAgent.id === LEAD.id ? (
                <div className="ptn-radial__empty">มอบหมายงานให้ทุกตำแหน่งในทีม ตามแผนที่แตกไว้</div>
              ) : outgoing.length === 0 ? (
                <div className="ptn-radial__empty">ปลายทางของสายงาน ส่งผลกลับให้ Tech Lead</div>
              ) : (
                outgoing.map((edge) => <FlowItem key={edge.key} agent={agentById[edge.to]} label={edge.label} type={edge.type} onPick={pick} />)
              )}
            </div>
            <div className="ptn-radial__col">
              <div className="ptn-radial__col-label">
                <i className="fa-solid fa-arrow-right-to-bracket" />
                รับงานจาก
              </div>
              {focusAgent.id === LEAD.id ? (
                <div className="ptn-radial__empty">รับผลงานกลับจากทุกตำแหน่ง แล้วรวบรวมเป็นสถานะเดียว</div>
              ) : (
                <>
                  <FlowItem agent={LEAD} label="มอบหมายงานตามแผน" type="handoff" onPick={pick} />
                  {incoming.map((edge) => (
                    <FlowItem key={edge.key} agent={agentById[edge.from]} label={edge.label} type={edge.type} onPick={pick} />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="ptn-radial__hint">
          <i className="fa-solid fa-hand-pointer me-1" />
          ชี้หรือคลิกที่ตำแหน่งเพื่อหยุดวงโคจร แล้วดูว่าส่งงานให้ใคร และรับงานจากใคร
        </div>
      )}
    </div>
  );
}
